import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../config/api';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Moon, Sun, Plus, Eye, Bug, Drop, Plant, Leaf,
  Warning, CheckCircle, Trash, MapPin, CalendarDots, Funnel,
  MagnifyingGlass, Camera, SpinnerGap
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AmazonDisclosure } from '../components/AmazonDisclosure';

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400', dot: 'bg-blue-500' },
  moderate: { label: 'Moderate', color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  severe: { label: 'Severe', color: 'bg-red-500/15 text-red-600 dark:text-red-400', dot: 'bg-red-500' },
};

const TYPE_ICONS = {
  'Fungus/Disease': Bug,
  'Water Buildup': Drop,
  'Dry/Brown Patches': Plant,
  'Pest Damage': Bug,
  'Weed Pressure': Leaf,
  'Drainage Issue': Drop,
};

// ── Create/Edit Dialog ──
const InspectionDialog = ({ open, onClose, zones, types, onCreated }) => {
  const [form, setForm] = useState({
    zone_id: '', zone_name: '', type: '', severity: 'moderate',
    description: '', date: new Date().toISOString().split('T')[0],
  });
  const [submitting, setSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compress image to max 1200px and JPEG quality 0.7 to stay under proxy limits
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = (ev) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_DIM = 1200;
        let w = img.width, h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          if (w > h) { h = Math.round(h * MAX_DIM / w); w = MAX_DIM; }
          else { w = Math.round(w * MAX_DIM / h); h = MAX_DIM; }
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const compressedUrl = canvas.toDataURL('image/jpeg', 0.7);
        setPhotoPreview(compressedUrl);
        setPhotoBase64(compressedUrl.split(',')[1]);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const runDiagnosis = async () => {
    if (!photoBase64) return;
    setDiagnosing(true);
    setDiagnosis(null);
    const doFetch = async () => {
      const res = await fetch(`${API_URL}/api/inspections/diagnose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          image_base64: photoBase64,
          zone_name: form.zone_name || 'Unknown',
        }),
      });
      if (res.status === 403) {
        const err = await res.json();
        toast.error(err.detail);
        return null;
      }
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Status ${res.status}`);
      }
      return res.json();
    };
    try {
      let data = await doFetch();
      if (!data) { setDiagnosing(false); return; }
      setDiagnosis(data);
      if (data.type && types.includes(data.type)) update('type', data.type);
      if (data.severity) update('severity', data.severity);
      if (data.identification && data.description) {
        update('description', `${data.identification}: ${data.description}`);
      }
      toast.success('AI diagnosis complete');
    } catch (err) {
      // Auto-retry once after 2 seconds (handles rate limits)
      try {
        toast('Retrying diagnosis...', { duration: 2000 });
        await new Promise(r => setTimeout(r, 2000));
        const data = await doFetch();
        if (!data) { setDiagnosing(false); return; }
        setDiagnosis(data);
        if (data.type && types.includes(data.type)) update('type', data.type);
        if (data.severity) update('severity', data.severity);
        if (data.identification && data.description) {
          update('description', `${data.identification}: ${data.description}`);
        }
        toast.success('AI diagnosis complete');
      } catch {
        toast.error('Diagnosis failed — please try again in a moment');
      }
    } finally {
      setDiagnosing(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.type || !form.description) {
      toast.error('Type and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.zone_id) {
        const zone = zones.find(z => z.id === payload.zone_id);
        if (zone) payload.zone_name = zone.name;
      }
      // Attach compressed photo so it's saved with the inspection record
      if (photoBase64) {
        payload.photo_base64 = photoBase64;
      }
      const res = await fetch(`${API_URL}/api/inspections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const data = await res.json();
      toast.success('Inspection logged');
      onCreated(data);
      onClose();
      setForm({ zone_id: '', zone_name: '', type: '', severity: 'moderate', description: '', date: new Date().toISOString().split('T')[0] });
      setPhotoPreview(null);
      setPhotoBase64(null);
      setDiagnosis(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Log Field Inspection</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Photo Upload + AI Diagnosis */}
          <div className="space-y-2" data-testid="insp-photo-section">
            <Label className="text-xs font-medium text-muted-foreground">Snap a Photo (AI will diagnose)</Label>
            <div className="flex gap-2">
              <label className="flex-1 cursor-pointer">
                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${photoPreview ? 'border-primary/40' : 'border-border hover:border-primary/30'}`}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="Lawn issue" className="w-full h-32 object-cover rounded-md" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                      <Camera size={24} />
                      <span className="text-xs">Tap to upload photo</span>
                    </div>
                  )}
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="insp-photo-input" />
              </label>
              {photoBase64 && (
                <Button
                  variant="default"
                  size="sm"
                  className="shrink-0 self-center gap-1"
                  onClick={runDiagnosis}
                  disabled={diagnosing}
                  data-testid="insp-diagnose-btn"
                >
                  {diagnosing ? <SpinnerGap size={14} className="animate-spin" /> : <Eye size={14} />}
                  {diagnosing ? 'Analyzing...' : 'Diagnose'}
                </Button>
              )}
            </div>
            {/* Diagnosis Result */}
            {diagnosis && (
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-2" data-testid="insp-diagnosis-result">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-primary">{diagnosis.identification}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    diagnosis.confidence === 'high' ? 'bg-emerald-500/15 text-emerald-600' :
                    diagnosis.confidence === 'medium' ? 'bg-amber-500/15 text-amber-600' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {diagnosis.confidence} confidence
                  </span>
                </div>
                {diagnosis.treatment && diagnosis.treatment.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Recommended Treatment</p>
                    <ul className="text-xs text-foreground/80 space-y-0.5 list-disc pl-4">
                      {diagnosis.treatment.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                )}
                {diagnosis.prevention && (
                  <p className="text-[10px] text-muted-foreground"><strong>Prevention:</strong> {diagnosis.prevention}</p>
                )}
                {diagnosis.timing && (
                  <p className="text-[10px] text-muted-foreground"><strong>Timing:</strong> {diagnosis.timing}</p>
                )}
                {/* Product Recommendations from TurfPulse Library */}
                {diagnosis.recommended_products?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-primary/10">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Recommended Products</p>
                    <div className="space-y-1.5">
                      {diagnosis.recommended_products.map((p, i) => (
                        <div key={i} className="flex items-center justify-between bg-background rounded-md px-2.5 py-1.5 border border-border/50" data-testid={`diag-product-${p.id}`}>
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="text-xs font-medium truncate">{p.name}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{p.active_ingredient}{p.unit_size ? ` — ${p.unit_size}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {p.cost_per_unit && (
                              <span className="text-xs font-semibold text-primary">${p.cost_per_unit.toFixed(2)}</span>
                            )}
                            {p.affiliate_link && (
                              <a href={p.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer" className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90" data-testid={`diag-buy-${p.id}`}>
                                Buy
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1 rounded-md bg-muted/40 border border-border/60 px-2 py-1.5">
                      <AmazonDisclosure variant="inline" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Zone */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Zone (optional)</Label>
            <Select value={form.zone_id} onValueChange={(v) => update('zone_id', v)}>
              <SelectTrigger data-testid="insp-zone">
                <SelectValue placeholder="Select zone or leave blank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific zone</SelectItem>
                {zones.map(z => (
                  <SelectItem key={z.id} value={z.id}>{z.name}{z.orientation ? ` (${z.orientation})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!form.zone_id && (
              <div className="mt-2">
                <Input
                  placeholder="Or type a location (e.g., North side of home)"
                  value={form.zone_name}
                  onChange={(e) => update('zone_name', e.target.value)}
                  className="text-sm"
                  data-testid="insp-zone-name"
                />
              </div>
            )}
          </div>

          {/* Type */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Issue Type</Label>
            <Select value={form.type} onValueChange={(v) => update('type', v)}>
              <SelectTrigger data-testid="insp-type">
                <SelectValue placeholder="What did you observe?" />
              </SelectTrigger>
              <SelectContent>
                {types.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Severity */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Severity</Label>
            <div className="flex gap-2 mt-1">
              {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => update('severity', key)}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium border transition-all
                    ${form.severity === key
                      ? `${cfg.color} border-current`
                      : 'bg-card border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  data-testid={`insp-severity-${key}`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Description</Label>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[100px] resize-y"
              placeholder="Describe what you observed — size, color, pattern, location details..."
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              data-testid="insp-description"
            />
          </div>

          {/* Date */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Date Observed</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => update('date', e.target.value)}
              data-testid="insp-date"
            />
          </div>

          <Button className="w-full" onClick={handleSubmit} disabled={submitting} data-testid="insp-submit">
            {submitting ? 'Logging...' : 'Log Inspection'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Resolve Dialog ──
const ResolveDialog = ({ open, onClose, inspection, onResolved }) => {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!inspection) return null;

  const handleResolve = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/inspections/${inspection.id}/resolve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) throw new Error();
      toast.success('Issue resolved');
      onResolved(inspection.id);
      onClose();
      setNotes('');
    } catch {
      toast.error('Failed to resolve');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Resolve: {inspection.type}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-muted-foreground">{inspection.zone_name} — {inspection.description}</p>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Resolution Notes (optional)</Label>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] resize-y"
              placeholder="What was done to fix it?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="resolve-notes"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1" onClick={handleResolve} disabled={submitting} data-testid="resolve-confirm">
              <CheckCircle size={16} className="mr-1" /> Mark Resolved
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Inspection Card ──
const InspectionCard = ({ inspection, onResolve, onDelete }) => {
  const sev = SEVERITY_CONFIG[inspection.severity] || SEVERITY_CONFIG.moderate;
  const Icon = TYPE_ICONS[inspection.type] || Eye;
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  const loadPhoto = async () => {
    if (photoUrl) { setPhotoOpen(true); return; }
    setLoadingPhoto(true);
    try {
      const res = await fetch(`${API_URL}/api/inspections/${inspection.id}/photo`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPhotoUrl(`data:image/jpeg;base64,${data.photo_base64}`);
        setPhotoOpen(true);
      } else {
        toast.error('Could not load photo');
      }
    } catch {
      toast.error('Could not load photo');
    } finally {
      setLoadingPhoto(false);
    }
  };

  return (
    <Card className={`border transition-all ${inspection.resolved ? 'opacity-60 border-border/30' : 'border-border/50 hover:border-primary/30'}`} data-testid={`inspection-${inspection.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${sev.color}`}>
            <Icon size={18} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className={`text-sm font-semibold text-foreground ${inspection.resolved ? 'line-through' : ''}`}>
                  {inspection.type}
                </h3>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {inspection.zone_name && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <MapPin size={10} /> {inspection.zone_name}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                    <CalendarDots size={10} /> {inspection.date}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${sev.color}`}>
                    {sev.label}
                  </span>
                  {inspection.resolved && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      Resolved
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{inspection.description}</p>
            {inspection.resolved && inspection.resolution_notes && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 italic">
                Resolution: {inspection.resolution_notes}
              </p>
            )}
            <div className="flex gap-2 mt-3">
              {inspection.has_photo && (
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={loadPhoto} disabled={loadingPhoto} data-testid={`view-photo-${inspection.id}`}>
                  {loadingPhoto ? <SpinnerGap size={12} className="mr-1 animate-spin" /> : <Camera size={12} className="mr-1" />}
                  Photo
                </Button>
              )}
              {!inspection.resolved && (
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onResolve(inspection)} data-testid={`resolve-${inspection.id}`}>
                  <CheckCircle size={12} className="mr-1" /> Resolve
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-xs h-7 text-muted-foreground" onClick={() => onDelete(inspection.id)} data-testid={`delete-${inspection.id}`}>
                <Trash size={12} className="mr-1" /> Remove
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-sm">{inspection.type} — {inspection.date}</DialogTitle>
          </DialogHeader>
          {photoUrl && <img src={photoUrl} alt="Inspection" className="w-full rounded-md" data-testid={`photo-img-${inspection.id}`} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Main Page ──
export const InspectionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [inspections, setInspections] = useState([]);
  const [zones, setZones] = useState([]);
  const [types, setTypes] = useState([]);
  const [summary, setSummary] = useState({ total_open: 0, total_severe: 0, zones: [] });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);

  // Filters
  const [filterZone, setFilterZone] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterResolved, setFilterResolved] = useState('false');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterZone !== 'all') params.append('zone_id', filterZone);
      if (filterType !== 'all') params.append('inspection_type', filterType);
      if (filterResolved !== 'all') params.append('resolved', filterResolved);

      const [inspRes, typesRes, zonesRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/inspections?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/inspections/types`, { credentials: 'include' }),
        fetch(`${API_URL}/api/property/zones`, { credentials: 'include' }),
        fetch(`${API_URL}/api/inspections/summary`, { credentials: 'include' }),
      ]);

      if (inspRes.ok) {
        const d = await inspRes.json();
        setInspections(d.inspections || []);
      }
      if (typesRes.ok) {
        const d = await typesRes.json();
        setTypes(d.types || []);
      }
      if (zonesRes.ok) {
        const d = await zonesRes.json();
        setZones(Array.isArray(d) ? d : d.zones || []);
      }
      if (summaryRes.ok) {
        const d = await summaryRes.json();
        setSummary(d);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [filterZone, filterType, filterResolved]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreated = (newInsp) => {
    setInspections(prev => [newInsp, ...prev]);
    setSummary(prev => ({ ...prev, total_open: prev.total_open + 1 }));
  };

  const handleResolved = (id) => {
    setInspections(prev => prev.map(i => i.id === id ? { ...i, resolved: true } : i));
    setSummary(prev => ({ ...prev, total_open: Math.max(0, prev.total_open - 1) }));
    setResolveTarget(null);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/inspections/${id}`, { method: 'DELETE', credentials: 'include' });
      setInspections(prev => prev.filter(i => i.id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="inspections-page">
      {/* Header */}
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/app')} data-testid="insp-back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <TurfPulseLogoSVG animated size={28} />
              <span className="font-semibold text-base tracking-tight hidden sm:inline">Field Inspections</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="insp-add-btn">
              <Plus size={16} className="mr-1" /> Log Inspection
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="insp-summary">
          <Card className="border border-border/50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{summary.total_open}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Open Issues</p>
            </CardContent>
          </Card>
          <Card className={`border ${summary.total_severe > 0 ? 'border-red-500/30 bg-red-500/5' : 'border-border/50'}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${summary.total_severe > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{summary.total_severe}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Severe</p>
            </CardContent>
          </Card>
          <Card className="border border-border/50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{summary.zones?.length || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Zones Affected</p>
            </CardContent>
          </Card>
        </div>

        {/* Zone alerts */}
        {summary.zones && summary.zones.length > 0 && (
          <div className="mb-6 space-y-2" data-testid="insp-zone-alerts">
            {summary.zones.filter(z => z.severe_count > 0).map(z => (
              <div key={z.zone_id || z.zone_name} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs">
                <Warning size={14} className="text-red-500 shrink-0" />
                <span className="text-red-700 dark:text-red-400">
                  <strong>{z.zone_name}</strong>: {z.severe_count} severe issue{z.severe_count > 1 ? 's' : ''} — {z.types.join(', ')}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="insp-filters">
          <Funnel size={14} className="text-muted-foreground" />
          <Select value={filterZone} onValueChange={setFilterZone}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="insp-filter-zone">
              <SelectValue placeholder="Zone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Zones</SelectItem>
              {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="insp-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterResolved} onValueChange={setFilterResolved}>
            <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="insp-filter-resolved">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">Open</SelectItem>
              <SelectItem value="true">Resolved</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Inspections List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : inspections.length > 0 ? (
          <div className="space-y-3" data-testid="insp-list">
            {inspections.map(insp => (
              <InspectionCard
                key={insp.id}
                inspection={insp}
                onResolve={setResolveTarget}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="insp-empty">
            <MagnifyingGlass size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-1">No inspections logged</h3>
            <p className="text-sm text-muted-foreground mb-4">Walk your yard and log what you see — fungi, dry spots, pest damage, water issues.</p>
            <Button onClick={() => setShowCreate(true)}>
              <Plus size={16} className="mr-1" /> Log Your First Inspection
            </Button>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <InspectionDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        zones={zones}
        types={types}
        onCreated={handleCreated}
      />
      <ResolveDialog
        open={!!resolveTarget}
        onClose={() => setResolveTarget(null)}
        inspection={resolveTarget}
        onResolved={handleResolved}
      />
    </div>
  );
};
